<template>
    <div
        v-if="isRenderable"
        class="product"
        :class="{
            'block': block,
        }"
    >
        <div
            v-if="isGuaranteedClean || recommendedByClinic"
            class="tag"
            :class="{
                guarantee: isGuaranteedClean,
                recommended: recommendedByClinic,
            }"
        >
            <h6 v-if="recommendedByClinic">{{ $t('products.recommended') }}</h6>
            <h6 v-else-if="isGuaranteedClean">{{ $t('products.guarantee') }}</h6>
        </div>
        <div
            v-if="computedProduct"
            class="content"
            :class="{
                highlight: clinic.recommendedProductId === computedProduct.code,
            }"
        >
            <div class="image-container">
                <DSAdeonaImage
                    v-if="computedProduct.attributes?.product_name"
                    :image="computedProduct.image"
                    :alt="$t('products.graphic', { productName: computedProduct.attributes.product_name })"
                />
            </div>
            <div class="product-info">
                <div class="product-name">
                    <h2 class="h4 name">{{ computedProduct.attributes?.product_name }}</h2>
                    <div class="drops" :aria-label="$t('products.drops', { drops: computedProduct.attributes?.drops?.length ?? 0 })">
                        <DSFontIcon
                            v-for="(,key) in computedProduct.attributes?.drops"
                            :key="`drop-${key}`"
                            code="drop"
                            class="drop"
                            :class="`drop-${key}`"
                            aria-hidden="true"
                            size="xl"
                            aria-label="A single 'drop' graphic visualizing the effieciency of the particular car wash product"
                        />
                    </div>
                    <p
                        v-if="computedProduct.attributes?.short_description"
                        class="short-description"
                    >
                        {{ computedProduct.attributes?.short_description }}
                    </p>
                </div>
                <DSPrice
                    v-if="computedProducts"
                    :products="computedProducts"
                    @add-product="addProduct"
                />
                <div class="disclaimer">
                    <DSFontIcon code="exclamation" size="xxxl" aria-hidden="true" />
                    <div>
                        <i18n-t
                            scope="global"
                            keypath="products.disclaimer"
                        >
                            <template #ennenPesua>
                                <span style="color: #e4031f">
                                    <i18n-t scope="global" keypath="products.disclaimerBefore"></i18n-t>
                                </span>
                            </template>
                        </i18n-t>
                    </div>
                </div>
            </div>
            <template v-if="props.block">
                <div class="info">
                    <div class="info-content">
                        <DSFontIcon
                            class="info-icon"
                            code="carwash"
                            icon-style="line"
                            aria-hidden="true"
                            size="xxl"
                        />
                        <div class="info-text">{{ $t('products.info1') }}</div>
                        <NuxtLink class="info-link" :to="$paths.getPath('stationLocator')">{{ $t('products.findNearest') }}</NuxtLink>
                    </div>
                    <div class="info-content">
                        <DSFontIcon
                            class="info-icon"
                            code="mobile"
                            icon-style="line"
                            aria-hidden="true"
                            size="xxl"
                        />
                        <div class="info-text">{{ $t('products.info2') }}</div>
                        <NuxtLink class="info-link" :to="$paths.getPath('st1wayApp')">{{ $t('products.readMoreApp') }}</NuxtLink>
                    </div>
                    <div class="info-content">
                        <DSFontIcon
                            class="info-icon"
                            code="book"
                            icon-style="line"
                            aria-hidden="true"
                            size="xxl"
                        />
                        <div class="info-text">{{ $t('products.info3') }}</div>
                        <NuxtLink class="info-link" :to="$paths.getPath('washingInstructions')">{{ $t('products.readMore') }}</NuxtLink>
                    </div>
                </div>
                <div class="description">
                    {{ computedProduct.attributes?.long_description }}
                </div>
            </template>
            <div
                v-if="computedProduct.attributes?.features?.length > 0"
                class="features"
                :class="{
                    'expanded': expanded.includes(computedProduct.id),
                }"
            >
                <h2 class="title h4">{{ $t('products.featuresTitle') }}</h2>
                <div class="list">
                    <div class="inner">
                        <div
                            v-for="(feature, key) in computedProduct.attributes.features"
                            :key="key"
                            class="feature"
                        >
                            <DSFontIcon
                                code="check"
                                icon-style="line"
                                class="primary"
                                :aria-label="$t('products.includes')"
                                aria-hidden="true"
                                size="l"
                            />
                            <div class="feature-name">{{ $t(`products.features.${feature}`) }}</div>
                        </div>
                    </div>
                </div>
                <div v-if="computedProduct.attributes?.features?.length > 5" class="expand" @click="toggleExpand(computedProduct.id)">
                    <div>{{ expanded.includes(computedProduct.id) ? $t('products.shrink') : $t('products.expand') }}</div>
                    <DSFontIcon
                        code="chevron-down"
                        icon-style="line"
                        size="l"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { useI18n, ref, computed, useNuxtApp, storeToRefs, useAnalytics } from '#imports';
import { useUserStore } from '~dsc/stores/cms/user';
import { useCarwashClinicStore } from '~dsc/stores/cms/carwashclinic';
const clinic = useCarwashClinicStore();
const { $cms, $analytics } = useNuxtApp();
const { loggedIn } = storeToRefs(useUserStore());
const { gtm } = useAnalytics();

const props = defineProps({
    id: {
        type: String,
        required: false,
        default: '',
    },
    internal: {
        type: String,
        required: false,
        default: 'Product Page',
    },
    title: {
        type: String,
        required: false,
        default: '',
    },
    contentType: {
        type: String,
        required: false,
        default: 'cardList',
    },
    display: {
        type: Object,
        required: false,
        default: () => ({
            component: 'product',
        }),
    },
    block: {
        type: Boolean,
        required: false,
        default: true,
    },
    cards: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const emits = defineEmits(['addProduct']);
const expanded = ref([]);

const isRenderable = computed(() => props.cards?.[0]?.products);

const computedCard = computed(() => {
    const { 0: card } = { ...props.cards };

    if (card) return card;

    return {};
});

const computedProduct = computed(() => {
    if (Object.keys(computedCard.value).length > 0) {
        const articleProduct = computedCard.value.products?.find(item => !item.isBundle);

        if (articleProduct) return articleProduct;
    }

    return computedCard.value?.products?.[0];
});

const computedProducts = computed(() => computedCard?.value?.products);

function toggleExpand(id) {
    if (expanded.value.includes(id)){
        expanded.value.splice(expanded.value.indexOf(id), 1);

        return;
    }

    expanded.value.push(id);
}

const isGuaranteedClean = computed(() => computedProduct.value.guarantee);

const recommendedByClinic = computed(() => clinic.recommendedProductId === computedProduct.value.code);

function addProduct(product) {
    gtm().trackEvent({
        event: 'add_to_cart',
        event_function: 'general',
        logged_in_status: loggedIn.value ? 'logged_in' : 'logged_out',
        user_type: 'consumer',
        action: 'click',
        currency: product.attributes?.currency?.[0] ?? $analytics.getCurrency(),
        value: $analytics.priceWithoutTax(product),
        items: [$analytics.productToAnalytics(product)],
    });
    emits('addProduct', product);
    $cms.addProduct(product);
}

useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            products: {
                save: 'Säästä',
                recommended: 'Sopivin pesu autollesi',
                guarantee: 'Takuulla puhdasta',
                pcs: 'kpl',
                buy: 'Osta',
                disclaimer: 'Tämä pesu ei sovi autoille, joissa on suksiboksi. Suksiboksipesu on ostettavissa vain asemalla. Ota yhteyttä henkilökuntaan {ennenPesua}, mikäli autossasi on ulkoinen vararengas, karjapuskuri, lisävalot tai vastaavia lisävarusteita tai autosi on pick-up, avoauto tai muu erikoisauto.',
                disclaimerBefore: 'ennen pesua',
                info1: 'Pesukoodit ovat voimassa 5 kuukautta kaikilla St1 Autopesuasemilla.',
                info2: 'Nopea tapa hyödyntää pesuedut – lataa äppi!',
                info3: 'Miten St1 Autopesu toimii? Tutustu autopesuohjeisiimme.',
                findNearest: 'Etsi lähin autopesula',
                readMore: 'Lue lisää',
                readMoreApp: 'Tutustu sovellukseen',
                featuresTitle: 'Pesuohjelma sisältää:',
                features: {
                    'Carnauba': 'Carnauba-vahaus',
                    'Drying': 'Kuivaus',
                    'soft-brush': 'Pehmoharjaus',
                    'chassis-wash': 'Alustanpesu',
                    'prewash': 'Esipesuaine',
                    'double-prewash': 'Tuplaesipesuaine',
                    'rim wash': 'Vannepesu',
                    'double-rim-wash': 'TUPLAvannepesu',
                    'efficient-drying': 'TEHOkuivaus',
                    'extra-impact-time': 'Lisävaikutusaika',
                    'foam': 'Pesuvaahto',
                    'high-pressure-wash': 'Korkeapainepesu',
                    'polishing wax': 'Kiillotusvahaus',
                    'soft-brush-polish': 'Pehmokiillotus',
                    'Rainlux': 'Rainlux-pinnoite',
                    'double drying': 'TUPLAkuivaus',
                    'double high pressure wash': 'TUPLAkorkeapainepesu',
                    'Double soft brush wash': 'Tuplapehmoharjaus',
                    'Ceramic': 'Ceramic-vaha',
                    'Quick drying': 'Nopea kuivaus',
                },
                expand: 'Näytä enemmän',
                shrink: 'Näytä vähemmän',
                drops: 'Pesun tehokkuus {drops} pisaraa',
                includes: 'Sisältää',
                graphic: 'Tuotteen "{productName}" kuva',
            },
        },
        'en-EU': {
            products: {
                save: 'Save',
                recommended: 'Recommended wash',
                guarantee: 'Clean guarante',
                pcs: 'pcs',
                buy: 'Buy',
                expand: 'Näytä enemmän',
                shrink: 'Näytä vähemmän',
                graphic: 'A graphic of {productName}',
            },
        },
    },
});
</script>
<style scoped>
.product {
  position: relative;
  justify-content: center;
  align-items: center;
  max-width: calc(100dvw - 4rem);
}
@media (max-width: 1199px) {
  .product {
    width: 100%;
  }
}
.product .tag {
  height: calc(2rem - 2px);
  display: grid;
  align-items: center;
  padding: 0.25rem 0.5rem;
  text-align: center;
  overflow: hidden;
  white-space: nowrap;
  width: fit-content;
  margin: 0 auto;
}
.product .tag h6 {
  text-transform: uppercase;
  font-size: 12px;
}
.product .prices {
  min-height: 8rem;
}
.product .guarantee {
  background-color: var(--color-primary-800);
}
.product .recommended {
  background-color: var(--color-success-300);
  color: var(--color-base-white);
}
.product .content {
  border: 1px solid var(--color-base-grey-300);
  border-radius: 0.375rem;
  margin: 0 auto;
  justify-content: center;
  align-self: center;
  text-align: center;
  width: 100%;
  overflow: hidden;
}
.product .content > div {
  padding: 0 1rem;
}
.product .content .image-container {
  height: 14rem;
  border-bottom: 1px solid var(--color-base-grey-300);
  margin-bottom: 1rem;
}
.product .content .image-container img {
  -webkit-user-drag: none;
  user-select: none;
}
.product .content .product-name {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  overflow-y: hidden;
}
.product .content .product-name .h4 {
  margin: 0;
  display: inline-flex;
  justify-self: start;
  align-items: center;
  color: var(--color-base-grey-800);
  text-align: left;
  line-height: 1.5rem;
  height: 3rem;
}
.product .content .product-name .short-description {
  grid-column-end: span 2;
  margin: 0.5rem 0 0;
  text-align: left;
  color: var(--color-base-grey-600);
}
.product .content .disclaimer {
  display: none;
}
.product .content .description {
  display: none;
}
.product .content .info {
  display: none;
}
.product .content .features {
  border-top: 1px solid var(--color-base-grey-300);
  margin: 1rem 0;
  padding: 1rem;
}
.product .content .features .title {
  display: block;
  text-align: left;
}
.product .content .features .feature {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 1rem;
  padding: 0.25rem;
  white-space: nowrap;
  line-height: 1.5rem;
}
.product .content .features .feature .feature-name {
  display: grid;
  text-align: left;
  align-items: center;
}
.product .content .features .feature .feature-value {
  display: grid;
  align-items: center;
}
.product .content .features .expand {
  position: absolute;
  bottom: 0.125rem;
  left: 0.125rem;
  display: grid;
  align-items: center;
  grid-template-columns: 1fr auto;
  padding: 2rem 1rem 1rem 0;
  margin: -1px 1rem;
  width: calc(100% - 2.25rem);
  cursor: pointer;
  background: linear-gradient(180deg, transparent 10%, var(--color-base-white) 25%);
  text-align: left;
  font-weight: 700;
  color: var(--color-base-grey-800);
}
.product .content .features .expand .icon {
  transition: transform 0.2s ease;
}
.product:not(.block) .product-name .short-description {
  min-height: 3rem;
}
.product:not(.block) .features .list {
  height: 180px;
  overflow: hidden;
}
.product:not(.block) .features .list .inner {
  transition: max-height 0.5s ease-out;
  max-height: 180px;
}
.product:not(.block) .features.expanded .list {
  height: auto;
  padding-bottom: 1.5rem;
}
.product:not(.block) .features.expanded .list .inner {
  max-height: 400px;
}
.product:not(.block) .features.expanded .expand .icon {
  transform: rotate(-180deg);
}
.product .highlight {
  border: 2px solid var(--color-success-300);
  box-shadow: inset 3px 0 var(--color-success-300), inset 0 3px var(--color-success-300), inset 0 -3px var(--color-success-300), inset -3px 0 var(--color-success-300), inset -3px -3px 10px 0 var(--color-success-300), inset 3px 3px 10px 0 var(--color-success-300), 0 8px 24px 0 rgba(82, 97, 115, 0.14);
}
.product .highlight .expand {
  box-shadow: inset 0 -3px 10px -10px var(--color-success-300), inset 0 -10px 10px -10px var(--color-success-300);
  border-bottom: 3px solid var(--color-success-300);
}
.product.block {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.product.block .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.product.block .tag {
  display: none;
}
.product.block .content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 2rem 1.5rem;
  animation-name: none;
  border: none;
  box-shadow: none;
  padding: 4rem 0;
}
.product.block .content .image-container {
  height: fit-content;
  border: none;
}
.product.block .content .product-info {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 2rem;
}
.product.block .content .product-name .short-description {
  display: block;
  text-align: left;
  font-weight: 500;
}
.product.block .content .features {
  border: none;
  grid-column-start: 1;
  grid-row-start: 3;
  height: auto;
  border-right: 1px solid var(--color-base-grey-200);
  padding-right: 1.5rem;
}
.product.block .content .features .title {
  display: block;
  text-align: left;
  font-size: 1.5rem;
  color: var(--color-base-black);
}
.product.block .content .disclaimer {
  display: grid;
  grid-template-columns: 1fr auto;
  text-align: left;
}
.product.block .content .disclaimer :nth-child(1) {
  fill: var(--color-primary-800);
}
.product.block .content .disclaimer :nth-child(2) {
  margin: 0 0.5rem;
}
.product.block .content .description {
  text-align: left;
  display: grid;
  font-size: 1.125rem;
  font-weight: 400;
  color: var(--color-base-grey-900);
  white-space: pre-line;
  line-height: 2rem;
  margin-top: 1rem;
}
.product.block .content .info {
  grid-column-end: span 2;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid var(--color-base-grey-200);
  padding-top: 2rem;
  text-align: left;
}
.product.block .content .info .info-content {
  display: grid;
  grid-template-columns: auto 1fr;
}
.product.block .content .info .info-content .info-icon {
  background-color: var(--color-primary-800);
  display: grid;
  padding: 0.5rem 0.75rem;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  margin: 0 1rem;
}
.product.block .content .info .info-content .info-link {
  font-weight: 900;
  text-decoration: underline;
  grid-column-start: 2;
  grid-row-start: 2;
  width: fit-content;
}
.product.block .content .expand {
  display: none;
}
@media (max-width: 1199px) {
  .product.block .content {
    grid-template-columns: 1fr;
    grid-gap: 0;
  }
  .product.block .content > * {
    grid-column-start: 1;
    grid-column-end: span 1;
  }
  .product.block .content .features {
    border-right: 0;
    padding-right: 0;
  }
  .product.block .content .info {
    grid-template-columns: 1fr;
    grid-gap: 2rem 0;
  }
  .product.block .content .description {
    margin-top: 2rem;
  }
}

.primary {
  color: var(--color-primary-900);
}

.drops {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.drops .drop {
  margin: 0.25rem 0;
}
.drops .drop-0 {
  color: #1D9ED5;
}
.drops .drop-1 {
  color: #1A8DBE;
}
.drops .drop-2 {
  color: #177DA8;
}
.drops .drop-3 {
  color: #146C91;
}
.drops .drop-4 {
  color: #115B7B;
}
</style>
